import { getEditor } from "@/utils/useEditor"
import { store } from "@/store/store"
import { photosViewAtom } from "@/store/photo/photos-view-state"
import {
	currentPhotoAtom,
	currentPhotoIdAtom,
	currentPhotoZoomAtom,
} from "@/store/photo/photos-state"
import { currentAdjustmentsAtom } from "@/store/store-history"
import { maskDetectionCoordsVersionAtom, maskDetectionStateAtom } from "@/store/editor/masks-state"
import { loadingStateAtom } from "@/store/app-state"
import { isPhotoLoadedIntoEditorAtom, photoLoadingStateAtom } from "@/store/editor/photo-loading"
import {
	maskOverlayIdAtom,
	maskOverlayPreviewMasksAtom,
	maskOverlayPreviewStateAtom,
} from "@/store/editor/mask-overlays"
import { highlightsClippingAtom, shadowsClippingAtom } from "../settings-state"
import { currentPresetAtom } from "../store-presets"
import {
	currentHoverPreviewAtom,
	PreviewableHistory,
	PreviewablePreset,
	previewHistory,
	previewPreset,
	resetPreview,
} from "./photo-preview"
import { isDeepEqual } from "@/utils/object"
import { debounce } from "@/utils/debounce"
import { MainAIMaskResolver } from "@/io/assets/ai-mask/main-ai-mask-resolver"
import { updatePhotoBorderColorForAIDebug } from "../photo/photos-state"
import { isAIDebugEnabled } from "@/utils/measurement/feature-flag"
import { imageInfosAtom } from "../processing/image-info"
import { MainMaskOverlayController } from "./main-mask-overlay-controller"

export function setupStoreEditorSubscribers() {
	const editor = getEditor()

	store.sub(photosViewAtom, () => {
		const previewState = store.get(photosViewAtom)

		switch (previewState) {
			case "before/after":
				editor.setViewMode("compare")
				break
			default:
				editor.setViewMode("after")
		}
	})

	// Re-apply adjustments after a photo finishes loading to catch any updates that happened mid-load
	store.sub(photoLoadingStateAtom, () => {
		const loadingState = store.get(photoLoadingStateAtom)
		if (loadingState !== "loaded") {
			return
		}

		const currentPhoto = store.get(currentPhotoAtom)
		if (!currentPhoto || editor.photo.id !== currentPhoto.id) {
			return
		}

		const currentAdjustments = store.get(currentAdjustmentsAtom)
		if (isDeepEqual(currentAdjustments, editor.adjustments)) {
			return
		}

		editor.setAdjustments(currentAdjustments)
	})

	store.sub(currentAdjustmentsAtom, () => {
		const currentPhoto = store.get(currentPhotoAtom)
		const currentAdjustments = store.get(currentAdjustmentsAtom)
		const loadingState = store.get(photoLoadingStateAtom)

		if (loadingState === "loading") {
			return
		}

		if (editor.photo.id !== currentPhoto?.id) {
			return
		}

		if (isDeepEqual(currentAdjustments, editor.adjustments)) {
			return
		}

		console.log("currentAdjustments updated")

		editor.setAdjustments(currentAdjustments)
	})

	/**
	 * Handle segmentation mask detection
	 */

	// TODO @din: move to the proper component
	const updateDetectionState = () => {
		const detectionState = MainAIMaskResolver.maskDetectionState

		store.set(maskDetectionStateAtom, { ...detectionState })
	}

	// TODO @din: move to the proper component
	MainAIMaskResolver.events.subscribe((event) => {
		const isExporting = store.get(loadingStateAtom).status === "exporting"

		if (isExporting) {
			return
		}
		if (event.type === "detection-coordinates-changed") {
			store.set(maskDetectionCoordsVersionAtom, (version) => version + 1)
			return
		}

		const mask = event.payload
		if (!mask) {
			return
		}

		switch (event.type) {
			case "detection-started":
			case "detection-canceled":
			case "detection-failed":
				updateDetectionState()
				break

			case "detection-succeeded":
				updateDetectionState()
				// Re-render immediately after object/segmentation mask is loaded
				editor.render({ reason: "ai-mask-detection-succeeded" })
		}
	})

	/**
	 * Handle editor zoom level in UI
	 */

	const updateZoomLevel = () => {
		const loadingState = store.get(photoLoadingStateAtom)

		if (loadingState === "loaded" && editor.zoom !== 0) {
			const currentZoom = editor.mainViewport.relativeZoom === 1 ? "fit" : editor.zoom
			store.set(currentPhotoZoomAtom, currentZoom)
		}
	}

	store.sub(isPhotoLoadedIntoEditorAtom, updateZoomLevel)

	editor.events.subscribe(updateZoomLevel)

	/**
	 * Handle masking overlays
	 */

	const updateMaskOverlays = () => {
		const overlayId = store.get(maskOverlayIdAtom)
		const previewMasks = store.get(maskOverlayPreviewMasksAtom)
		MainMaskOverlayController.schedule(overlayId ?? null, previewMasks)
	}

	store.sub(maskOverlayIdAtom, updateMaskOverlays)
	store.sub(maskOverlayPreviewMasksAtom, updateMaskOverlays)
	store.sub(currentPhotoIdAtom, () => {
		store.set(maskOverlayPreviewStateAtom, { type: "reset" })
		MainMaskOverlayController.reset()
	})

	/**
	 * Handle hover to preview preset and history
	 */

	store.sub(
		currentHoverPreviewAtom,
		debounce(() => {
			const currentPreview = store.get(currentHoverPreviewAtom)

			if (currentPreview == null) {
				resetPreview()
			} else if (currentPreview?.type === "preset") {
				const currentPreviewPreset = currentPreview as PreviewablePreset
				if (currentPreviewPreset.id !== store.get(currentPresetAtom)?.preset?.id) {
					// Preview preset
					previewPreset(currentPreviewPreset)
				}
			} else if (currentPreview?.type === "history") {
				const currentPreviewHistory = currentPreview as PreviewableHistory

				if (currentPreviewHistory.adjustments === editor.adjustments) {
					resetPreview()
				} else {
					// Preview history
					previewHistory(currentPreviewHistory)
				}
			}
		}, 20),
	)

	const updateClipping = () => {
		const highlightsClipping = store.get(highlightsClippingAtom)
		const shadowsClipping = store.get(shadowsClippingAtom)

		editor.toggleColorClipping(highlightsClipping, shadowsClipping)
	}

	store.sub(highlightsClippingAtom, updateClipping)
	store.sub(shadowsClippingAtom, updateClipping)

	// Always subscribe to photo/imageInfo changes for debug borders
	// The callback checks isAIDebugEnabled() internally, so it's safe to always subscribe
	store.sub(
		currentPhotoAtom,
		debounce(() => {
			if (isAIDebugEnabled()) {
				updatePhotoBorderColorForAIDebug()
			}
		}, 20),
	)

	store.sub(
		imageInfosAtom,
		debounce(() => {
			if (isAIDebugEnabled()) {
				updatePhotoBorderColorForAIDebug()
			}
		}, 20),
	)

	// Setup editor states with store atoms
	updateClipping() // update histogram clipping on editor init
	updateMaskOverlays()
}
